import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/admin/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  register: (userData) => api.post('/auth/register', userData),
  getMe: () => api.get('/auth/me'),
  changePassword: (passwords) => api.post('/auth/change-password', passwords),
};

// Services API
export const servicesAPI = {
  getAll: () => api.get('/services'),
  getAllAdmin: () => api.get('/services/admin'),
  getById: (id) => api.get(`/services/${id}`),
  create: (data) => api.post('/services', data),
  update: (id, data) => api.put(`/services/${id}`, data),
  delete: (id) => api.delete(`/services/${id}`),
};

// Testimonials API
export const testimonialsAPI = {
  getAll: (featured = false) => api.get(`/testimonials${featured ? '?featured=true' : ''}`),
  getAllAdmin: () => api.get('/testimonials/admin'),
  getById: (id) => api.get(`/testimonials/${id}`),
  create: (data) => api.post('/testimonials', data),
  update: (id, data) => api.put(`/testimonials/${id}`, data),
  delete: (id) => api.delete(`/testimonials/${id}`),
};

// Blog API
export const blogAPI = {
  getAll: (params = {}, lang = 'en') => api.get('/blog', { params: { ...params, lang } }),
  getAllAdmin: (params = {}) => api.get('/blog/admin', { params }),
  getBySlug: (slug, lang = 'en') => api.get(`/blog/${slug}`, { params: { lang } }),
  getCategories: () => api.get('/blog/categories'),
  create: (data) => api.post('/blog', data),
  update: (id, data) => api.put(`/blog/${id}`, data),
  delete: (id) => api.delete(`/blog/${id}`),
};

// Content API
export const contentAPI = {
  getAll: (keys) => api.get('/content', { params: { keys: keys?.join(',') } }),
  getAllAdmin: () => api.get('/content/admin'),
  getByKey: (key) => api.get(`/content/${key}`),
  createOrUpdate: (data) => api.post('/content', data),
  update: (key, data) => api.put(`/content/${key}`, data),
  delete: (key) => api.delete(`/content/${key}`),
};

// Newsletter API
export const newsletterAPI = {
  subscribe: (data) => api.post('/newsletter/subscribe', data),
  unsubscribe: (email) => api.post('/newsletter/unsubscribe', { email }),
  getSubscribers: (params = {}) => api.get('/newsletter/subscribers', { params }),
};

// Contact API
export const contactAPI = {
  sendMessage: (data) => api.post('/contact', data),
};

// Upload API
export const uploadAPI = {
  uploadImage: (formData) => {
    const config = {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    };
    return api.post('/upload/image', formData, config);
  },
  uploadImages: (formData) => {
    const config = {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    };
    return api.post('/upload/images', formData, config);
  },
  deleteImage: (filename) => api.delete(`/upload/image/${filename}`),
};

export default api;
